import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
    getTemplateList,
    initGetTemplateList,
    setVersionsLoadedFlag,
    updatePageAndGetList,
    updateSortAndGetList,
    getTemplateReferences,
    clearTemplateVersions
} from "../../actions/templateListActions";
import { openDrawer } from "../../actions/drawerActions";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import ReferencesDrawerHook, { referencesDrawerProps } from "../drawers/ReferencesDrawerHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import ReviewDrawer, { reviewDrawerProps } from "../drawers/ReviewDrawerHook";
import ActionReviewDrawerHook, { actionReviewDrawerProps } from "../drawers/ActionReviewDrawerHook";
import formatCopyText from "../../utilities/formatCopyText";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import {setQuestionnaireVersionsModeFlag} from "../../actions/questionnaireTemplateActions";

const pageTitle = {
    "questionnaire": "Questionnaire",
    "category": "Category",
    "group": "Group",
    "question": "Question",
    "action": "Action"
};

function TemplateVersionsList(props) {
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const { templateKey: routeTemplateKey } = useParams();

    const dispatch = useDispatch();
    const versionsList = useSelector(state => state.templateVersions);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER[props.templateMode][props.templateType]]);
    const versionsLoadedFlag = useSelector(state => state.versionsListLoadedFlag.flag);

    useEffect(() => {
        setLoading(true);
        dispatch(setQuestionnaireVersionsModeFlag(true));
        dispatch(setVersionsLoadedFlag(false)); // Used to force reload

        return () => {
            dispatch(clearTemplateVersions());
        };
    }, [dispatch]);

    useEffect(() => {

        if (routeTemplateKey && (!versionsLoadedFlag || routeTemplateKey !== filterState.templateKey)) {
            setLoading(true);
            setFilter(true);
            dispatch(setVersionsLoadedFlag(true));
            dispatch(initGetTemplateList(filterState, props.templateType, props.templateMode, routeTemplateKey));
        }
    }, [routeTemplateKey, filterState, dispatch, props.templateType, props.templateMode, versionsLoadedFlag]);

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        }
        else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
    }, [loading, filter, versionsList]);


    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setLoading(true);
            setFilter(false);
            callback();
        } else {
            setLoading(true);
            setFilter(false);
            callback(parm1);
        }
    };

    const onPageChange = (page, size, event) => {
        setLoading(true);
        setFilter(true);
        dispatch(updatePageAndGetList(page, size, filterState, props.templateType, props.templateMode, event));
    };

    const onSortChange = (sort, direction) => {
        setLoading(true);
        setFilter(true);
        dispatch(updateSortAndGetList(sort, direction, filterState, props.templateType, props.templateMode));
    };

    const viewReferences = (templateKey, id, p1, p2, row) => {
        let title = TEMPLATE.display[props.templateType] + ":";
        dispatch(getTemplateReferences(props.templateType, id));
        dispatch(openDrawer(referencesDrawerProps, <ReferencesDrawerHook templateId={id} templateType={props.templateType} row={row} title={title} />));
    };

    const openReviewDrawer = useCallback((templateKey, id, name, row, event) => {
        const resolvedTemplateKey = templateKey || (row && row.templateKey);
        const resolvedId = id || (row && row.id);
        const label = pageTitle[props.templateType] + ":";
        formatCopyText(row, event, label);

        if (props.templateType === TEMPLATE.TYPE.ACTION) {
            dispatch(openDrawer(actionReviewDrawerProps, <ActionReviewDrawerHook templateKey={resolvedTemplateKey} templateId={resolvedId} />));
            return;
        }

        const templateId = props.templateType === TEMPLATE.TYPE.QUESTIONNAIRE ? parseInt(resolvedId) : resolvedId;
        dispatch(openDrawer(reviewDrawerProps, <ReviewDrawer templateId={templateId} templateKey={resolvedTemplateKey} templateType={props.templateType} />));
    }, [dispatch, props.templateType]);

    let customActions = [];
    if (filterState.defaultActions && typeof filterState.defaultActions.edit === "function") {
        customActions.push({
            func: filterState.defaultActions.edit,
            label: "Edit"
        });
    }

    if (props.templateType === TEMPLATE.TYPE.CATEGORY || props.templateType === TEMPLATE.TYPE.GROUP || props.templateType === TEMPLATE.TYPE.QUESTION) {
        customActions.push({
            func: viewReferences,
            label: "References"
        });
    }

    return (
        <div id="TemplateVersionsList-container-div" style={{
            height: "100%",
            paddingTop: "15px",
            paddingLeft: "15px",
            paddingBottom: "15px",
            paddingRight: "15px"
        }}>

            <ArgoListTitleAndLoadingAnimationHook
                fileName="TemplateVersionsList"
                listTitle={pageTitle[props.templateType] + " Versions"}
                loading={loading}
                filter={filter}
                showOutstandingAlert={false}
            />


            <div>&nbsp;</div>

            <div id="TemplateVersionsList-title-container-div"
                style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>

                <div style={{ fontSize: "18px", fontWeight: "500" }}>{pageTitle[props.templateType] + " Versions"}</div>
                {/*<div style={{ width: "20px" }}>&nbsp;</div>*/}
                {/*<div style={{ color: "gray" }}>Name:</div>*/}
                {/*<div>&nbsp;</div>*/}
                {/*<div style={{ fontWeight: "500" }}>{filterState.templateName || (versionsList[0] && versionsList[0].name) || ""}</div>*/}

                {/*<div style={{ width: "20px" }}>&nbsp;</div>*/}
                {/*<div style={{ color: "gray" }}>Qualifier:</div>*/}
                {/*<div>&nbsp;</div>*/}
                {/*<div style={{ fontWeight: "500" }}>{(versionsList[0] ? versionsList[0].nameQualifier : "")}</div>*/}
            </div>

            <div>&nbsp;</div>

            <TableViewHook
                templateType={props.templateType}
                templateMode={props.templateMode}
                setLoadingState={setLoadingStateTrue}
                filter={<FilterPopOverHook page={TEMPLATE.FILTER[props.templateMode][props.templateType]}
                    templateType={props.templateType}
                    templateMode={props.templateMode}
                    filterName={TEMPLATE.FILTER[props.templateMode][props.templateType]}
                    submitValue={getTemplateList}
                    statuses={["All", "Draft", "Published"]}
                    setLoadingState={setLoadingStateTrue} parentName={"TemplateVersionsList"}
                />}>
                <TableGridHook 
                    displayType={props.templateType}
                    dataSource={versionsList}
                    customActions={customActions}
                    defaultActions={filterState.defaultActions}
                    pageState={filterState}
                    onPageChange={onPageChange}
                    sortState={filterState}
                    onSortChange={onSortChange}
                    nameDrawer={typeof filterState.defaultActions?.review === "function" ? filterState.defaultActions.review : openReviewDrawer}
                    templateType={props.templateType}
                    templateMode={TEMPLATE.MODE.VERSIONS}
                    showStartDate={props.templateType === TEMPLATE.TYPE.QUESTIONNAIRE}
                    showEndDate={props.templateType === TEMPLATE.TYPE.QUESTIONNAIRE}
                    showVersionCount={false}
                    showQualifier={true}
                    fileName={"TemplateVersionsList"}
                />
            </TableViewHook>
        </div>
    );
}

TemplateVersionsList.defaultProps = {
    templateMode: TEMPLATE.MODE.VERSIONS,
};

TemplateVersionsList.propTypes = {
    templateMode: PropTypes.string,
    templateType: PropTypes.string,
};

export default TemplateVersionsList;